import { useCallback, useEffect, useState } from 'react';
import { fetchSystemDashboardCockpit } from '../services/dashboard';
import type { SystemDashboardCockpitResponse } from '../types';

interface UseSystemDashboardCockpitOptions {
  dateFrom?: string | null;
  dateTo?: string | null;
  enabled?: boolean;
}

/**
 * Load the platform-wide cockpit snapshot with optional date filters.
 */
export function useSystemDashboardCockpit({
  dateFrom,
  dateTo,
  enabled = true,
}: UseSystemDashboardCockpitOptions = {}) {
  const [cockpit, setCockpit] = useState<SystemDashboardCockpitResponse | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async (mode: 'initial' | 'refresh' = 'initial') => {
    if (!enabled) {
      setCockpit(null);
      setError(null);
      setLoading(false);
      setRefreshing(false);
      return;
    }

    if (mode === 'refresh') {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await fetchSystemDashboardCockpit({ dateFrom, dateTo });
      setCockpit(response);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateFrom, dateTo, enabled]);

  useEffect(() => {
    void load('initial');
  }, [load]);

  return {
    cockpit,
    isLoading: loading,
    isRefreshing: refreshing,
    error,
    refresh: () => load('refresh'),
  };
}

export default useSystemDashboardCockpit;
